import React, { type PropsWithChildren, useState } from "react";

import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import type { FormQuestionType, RephraseQuestionType } from "@performyard/service-contracts";

import { useAiSettings } from "../../../api/hooks";

import Button from "../../Button";
import Icon from "../../Icon/Icon";
import { FormQuestionRephraseModule } from "../FormQuestionRephraseModule";
import { RephraseModule } from "../RephraseModule";
import { SummarizeReviewCycleModule } from "../ReviewCycleInsights";
import { SummarizeReviewModule } from "../SummarizeReview";
import { SurveyInsightModule } from "../SurveyInsight";

type AIButtonBaseProps = {
	onOpenChange?: (isOpen: boolean) => void;
	disabled?: boolean;
};

type RephraseModuleProps = {
	moduleType: "rephrase";
	buttonSize?: "small";
	question: string;
	subText: string;
	subject: string;
	author: string;
	answer: string[];
	questionType: RephraseQuestionType;
	onInsert: (newAnswer: string | string[]) => void;
	goalName?: string;
	context?: Record<string, unknown>;
};

type FormQuestionRephraseModuleProps = {
	moduleType: "formQuestionRephrase";
	buttonSize?: "small";
	input: string;
	questionType: FormQuestionType;
	onInsert: (newAnswer: string) => void;
};

type SurveyInsightsModuleProps = {
	moduleType: "surveyInsights";
	buttonSize?: "medium";
	surveyId: string;
};

type CycleInsightsModuleProps = {
	moduleType: "cycleInsights";
	buttonSize?: "medium";
	cycleId: string;
	authorCompletedReviewCount: number;
};

type ReviewInsightsModuleProps = {
	moduleType: "reviewInsights";
	buttonSize?: "medium";
	reviewId: string;
};

type InsightModuleProps = ReviewInsightsModuleProps | CycleInsightsModuleProps | SurveyInsightsModuleProps;

const ButtonLabel: Record<string, string> = {
	rephrase: "Rephrase",
	formQuestionRephrase: "Rephrase",
	surveyInsights: "AI Insights",
	reviewInsights: "Review Insights",
	cycleInsights: "Cycle Insights",
};

export type AIButtonProps =
	| (AIButtonBaseProps & RephraseModuleProps)
	| (AIButtonBaseProps & FormQuestionRephraseModuleProps)
	| (AIButtonBaseProps & InsightModuleProps);

/**
 * AIButton component
 */

export const AIButton = (props: PropsWithChildren<AIButtonProps>) => {
	const { onOpenChange, disabled, moduleType, buttonSize } = props;
	const [isOpen, setIsOpen] = useState(false);

	const { data: AiSettings } = useAiSettings();

	const open = () => {
		onOpenChange?.(true);
		setIsOpen(true);
	};

	const close = () => {
		onOpenChange?.(false);
		setIsOpen(false);
	};

	if (moduleType === "rephrase" && !AiSettings?.authoringAssistantEnabled) return null;
	if (moduleType === "formQuestionRephrase" && !AiSettings?.formQuestionAssistantEnabled) return null;
	if (moduleType === "surveyInsights" && !AiSettings?.surveyInsightsEnabled) return null;
	if (moduleType === "reviewInsights" && !AiSettings?.reviewSummarizationEnabled) return null;
	if (moduleType === "cycleInsights" && !AiSettings?.reviewCycleInsightsEnabled) return null;

	const iconColor =
		moduleType === "reviewInsights" || moduleType === "cycleInsights" || moduleType === "surveyInsights"
			? "neutral-1"
			: disabled
				? "neutral-4"
				: "primary-5";

	const labelColor = `text-${iconColor}`;

	return (
		<div>
			<Button
				onClick={open}
				color="blue"
				textOnly={moduleType === "rephrase"}
				disabled={disabled}
				size={buttonSize || "small"}
				testid="test-rephrase-button"
			>
				<div className="flex items-center space-x-2">
					<Icon icon={faSparkles} color={iconColor} size="x-small" />
					<span className={`text-sm font-normal ${labelColor}`}>{ButtonLabel[moduleType]}</span>
				</div>
			</Button>

			{isOpen && moduleType === "rephrase" && (
				<RephraseModule
					question={props.question}
					subText={props.subText}
					subject={props.subject}
					author={props.author}
					answer={props.answer}
					questionType={props.questionType}
					goalName={props.goalName}
					onInsert={props.onInsert}
					isOpen={isOpen}
					onOpenChange={close}
					context={props.context}
				/>
			)}

			{isOpen && moduleType === "formQuestionRephrase" && (
				<FormQuestionRephraseModule
					input={props.input}
					questionType={props.questionType}
					onInsert={props.onInsert}
					isOpen={isOpen}
					onOpenChange={close}
				/>
			)}

			{moduleType === "surveyInsights" && props.surveyId && (
				<SurveyInsightModule surveyId={props.surveyId} isOpen={isOpen} onOpenChange={close} />
			)}

			{moduleType === "reviewInsights" && (
				<SummarizeReviewModule reviewId={props.reviewId} isOpen={isOpen} onOpenChange={close} />
			)}

			{moduleType === "cycleInsights" && (
				<SummarizeReviewCycleModule
					reviewCycleId={props.cycleId}
					authorCompletedReviewCount={props.authorCompletedReviewCount}
					isOpen={isOpen}
					onOpenChange={close}
				/>
			)}
		</div>
	);
};
